import React, { useState, useRef, useEffect } from 'react';
import {
  MenuItem,
  Select,
  FormControl, InputLabel,
  Box,
  TextField,
  Typography,
  Button as PriButton
} from "@mui/material"
import {
  Wrapper,
  HeaderWrapper,
    Header,
} from "../SettingsSection/SettingsSection.styles"
import Button from "../Button/Button"
import { usePostRejectionEmailContent, uploadTemplateLogo } from '../../hooks/RejectionEmail'
import { openInvoices, selectLGInternalUser } from "../../activeScreenSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import TemplatePlaceholderRef from "../TemplatePlaceholderReference/TemplatePlaceholderReference"
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

type FieldErrors = Partial<Record<keyof any, string>>;

export default function RejEmailTemplate({opcosList}) {
    const LGInternalUser = useAppSelector(selectLGInternalUser);
    const [selectedBU, setSelectedBU] = useState("");
    const [subjectLine, setSubjectLine] = useState("");
    const fileInputRef = useRef(null);
    const [fileContent, setFileContent] = useState('');
    const [fileName, setFileName] = useState('');
    const [imgFileName, setImgFileName] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const logoInputRef = useRef(null);
    const mutation = usePostRejectionEmailContent();
    const uploadLogoMutation = uploadTemplateLogo();
    const [selectedId, setSelectedId] = useState('');
    const [errors, setErrors] = useState<any>({});
    const [showLogoUpload, setShowLogoUpload] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const validate = () => {
        const errs:FieldErrors = {};
            if(selectedBU.trim().length <= 0) {
                errs.opcoId = "This field is required";
            }
            if (subjectLine.trim().length <= 0) {
                errs.subject = "This field is required"
            }
            if (fileContent.trim().length <= 0) {
                errs.content = "This field is required"
            }
            if (showLogoUpload && imgFileName.trim().length <= 0) {
                errs.logo = "This field is required"
            }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    
    };

    const handleLogoBtnClick = () => {
        if (logoInputRef.current) {
            logoInputRef.current.click();
        }
    };

    const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];
        setImgFileName(file ? file.name : '');
        setLogoFile(file || null);
    };

    const handleSubmit = () => {
        if(validate()) {
            mutation.mutate({
                opco_id: `${selectedId}`,
                template_content: fileContent,
                email_subject: subjectLine
            })
            if(logoFile) {
                showLogoUpload && uploadLogoMutation.mutate({
                    opcoId: `${selectedId}`,
                    branding_image: logoFile, 
                })
            }
        }
    }

    const handleCancelClick = () => {
        dispatch(openInvoices())
    };

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection and reading
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setFileName(file ? file.name : '');
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                // TypeScript-safe: check that result is a string
                if (typeof e.target.result === 'string') {
                    setFileContent(e.target.result);
                } else {
                    setFileContent('');
                }
            };
            reader.readAsText(file);
        }
    };

  const onOpcoChange = (event) => {
    const selectedValue = event.target.value;
    const selectedOption = opcosList?.find(option => option.name === selectedValue);
    if (selectedOption) {
      setSelectedId(selectedOption.id);
      setSelectedBU(selectedOption.name);
    }
  };

  const handleTextFieldChange = (event) => {
    setFileContent(event.target.value);
    if(event?.target?.value?.length === 0) {
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            setFileName("");
        }
    }
    if(!showLogoUpload && event?.target?.value?.toLowerCase().includes('bu_logo')){
        setShowLogoUpload(true);
    } else if(!event?.target?.value?.toLowerCase().includes('bu_logo') && showLogoUpload) {
        setShowLogoUpload(false);
    }
  };

    return <Wrapper>
        <Box sx={{ flexDirection: 'row', display: 'flex', gap: 3}}>
            <Box sx={{width: "65%", flexDirection: 'column'}}>
                <HeaderWrapper sx={{mb: 3}}>
                    <Header>Rejection email</Header>
                </HeaderWrapper>
                <Box>
                    <FormControl
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={{ height: '40px', width: '350px' }}
                    >
                        <InputLabel id="opco-configuration-mode-label">Business Unit*</InputLabel>
                        <Select
                            labelId='opco-label'
                            name="opco"
                            value={selectedBU}
                            label="Business Unit code"
                            onChange={onOpcoChange}
                            required
                            error={!!errors.opcoId}
                            >
                            {opcosList?.map(option => (
                                <MenuItem key={option.id} id={option.id} value={option.name}>{option.name}</MenuItem>
                            ))}
                            
                        </Select>
                    </FormControl>
                </Box>
            <Box>
            <TextField
                label="Subject Line"
                name="subject-email"
                value={subjectLine}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSubjectLine(event.target.value);
                }}
                sx={{ height: '40px', width: '450px', mt: 2 }}
                size="small"
                InputLabelProps={{ shrink: true }}
                placeholder="Enter the subject for your email"
                disabled={LGInternalUser}
                variant="outlined"
                required
                error={!!errors.subject}
            />
            </Box>
            <Typography sx={{ mt: 2, mb: 2 }} variant="body2">Please upload a valid HTML file containing your email template</Typography>
            <Box>
                <TextField
                    label="Upload Email template file"
                    variant="outlined"
                    value={fileName}
                    InputProps={{
                        readOnly: true,
                    }}
                    size="small"
                    sx={{ width: 250 }}
                    disabled={LGInternalUser}
                />
            <input
                type="file"
                accept=".html, text/html"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
                required
            />
            <PriButton sx={{marginLeft: '20px', mt: '2px'}} variant="outlined" onClick={handleButtonClick} disabled={LGInternalUser} startIcon={<FileUploadOutlinedIcon />}>
                Browse
            </PriButton>
            <TextField
                label="File Content"
                multiline
                fullWidth
                minRows={25}
                value={fileContent}
                margin="normal"
                variant="outlined"
                onChange={handleTextFieldChange}
                required
                disabled={LGInternalUser}
                error={!!errors.content}
            />
            </Box>
        </Box>
    <Box sx={{width: '35%'}}><TemplatePlaceholderRef /></Box>
    </Box>
    {<Box sx={{display:"flex", flexDirection: 'column'}}  mt={3}>
        <Box>
            <Typography variant="body1">
                Please upload your business unit logo. Accepted file type is PNG only. Maximum file size is 100 KB.<br />
                For best results, use a square image (150 x 150 pixels) with a transparent background.
            </Typography>
        </Box>
        <Box mt={3}>
            <FormControl>
            <TextField
                label="Upload logo"
                variant="outlined"
                value={imgFileName}
                InputProps={{
                    readOnly: true,
                }}
                sx={{ flex: 1, mr: 2 }}
                size="small"
                required
                disabled={LGInternalUser}
                error={!!errors.logo}
            />
            <input
                type="file"
                accept="image/*"
                hidden
                required
                style={{ display: 'none' }}
                ref={logoInputRef}
                onChange={handleLogoChange}
            />
            </FormControl>
            <PriButton sx={{ mt: '2px'}} variant="outlined" onClick={handleLogoBtnClick} disabled={LGInternalUser} startIcon={<FileUploadOutlinedIcon />}>
                Browse
            </PriButton>
        </Box>
        </Box>}
        <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
            <PriButton
                variant="outlined"
                onClick={handleCancelClick}
                sx={{mr: 2}}
            >
                Cancel
            </PriButton>
            <PriButton
                variant="contained"
                onClick={handleSubmit}
                disabled={LGInternalUser}
            >
                Save
            </PriButton>
        </Box>
    </Box>
</Wrapper>                        
}
